import { Card, Col, Row, Typography, Descriptions, DatePicker, Tag, Input, Modal, Button, Tooltip } from "antd";
import { useContext, useState } from "react";
import { useNavigate } from "react-router";
import { useAuthContext } from "../../context/AuthContext";
import { formatPhone } from "../../helper/functions";
import lang from "../../helper/langHelper";
import useApi from "../../hooks/useApi";
import { allEligibles, getDashboardCount, getLeaderboardData, ListBooking, updateEligibleNote } from "../../constants/API/API";
import { AppStateContext } from "../../context/AppContext";
import CustomTable from "../../components/CustomTable";
import moment from "moment";
import { Severty, ShowToast } from "../../helper/toast";
import useFilter from "../../hooks/useFilter";
import UserAndBokkingChart from "./UserAndBookingChart";


const { Title } = Typography;
const { RangePicker } = DatePicker;

const columns = [
  {
    title: "Booking ID",
    dataIndex: "bookingId",
    key: "bookingId",
    render: (_, { bookingId }) => bookingId || "-",
  },
  {
    title: "Student",
    dataIndex: "user",
    key: "user",
    render: (text, record) => record?.user?.name || "-",
  },
  {
    title: "Class",
    dataIndex: "class",
    key: "class",
    render: (text, record) => record?.class?.name || "-",
  },
  {
    title: "Branch",
    dataIndex: "branch",
    key: "branch",
    render: (text, record) => record?.branch?.name || "-",
  },
  {
    title: "Trainer",
    dataIndex: "trainer",
    key: "trainer",
    render: (text, record) => record?.trainer?.name || "-",
  },
  {
    title: "Date",
    dataIndex: "date",
    key: "date",
    render: (text, record) =>
      record?.date ? moment(record?.date).format("ll") : "-",
  },
  {
    title: "Time Slot",
    dataIndex: "slot",
    key: "slot",
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    render: (value) => {
      const colorMap = {
        Completed: "green",
        Missed: "red",
        Accepted: "blue",
        Rescheduled: "orange",
        Cancelled: "volcano",
        Pending: "gold",
      };

      return <Tag color={colorMap[value] || "default"}>{value || "-"}</Tag>;
    },
  },
  {
    title: "Created At",
    dataIndex: "createdAt",
    key: "createdAt",
    render: (text) => (text ? new Date(text).toLocaleDateString() : "-"),
  },
];



const leaderboardColumns = [
  {
    title: "Student",
    dataIndex: "name",
    key: "studentName",
  },
  {
    title: "Score",
    dataIndex: "performance",
    key: "performance",
  },
  {
    title: "Current Belt",
    dataIndex: ["beltId", "name"],
    key: "belt",
  },
  {
    title: "Current Kyu",
    dataIndex: ["kyuId", "name"],
    key: "kyu",
  },
  {
    title: "Branch",
    dataIndex: ["branchId", "name"],
    key: "branch",
  },
];

function Home() {
  const { setPageHeading } = useContext(AppStateContext);
  setPageHeading("Dashboard");
  const [loading, setLoading] = useState(false);
  const { userProfile } = useAuthContext();
  const [dashboard, setDashboard] = useState();
  const [bookings, setBookings] = useState([]);
  const [eligibles, setEligibles] = useState([]);
  const [totalEligibles, setTotalEligibles] = useState(0);
  const [eligiblesFilters, setEligiblesFilters] = useState({
    page: 1,
    limit: 10,
  });
  const [examOverview, setExamOverview] = useState({
    totalUpcomingExamCapacity: 0,
    totalReservedExamSpots: 0,
    totalEligibleStudents: 0,
    eligibleStudentsWithoutReservation: 0,
  });

  const [selectedStudentForNote, setSelectedStudentForNote] = useState(null);
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [noteInputText, setNoteInputText] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const openNoteModal = (record) => {
    setSelectedStudentForNote(record);
    setNoteInputText(record?.adminNotes || "");
    setNoteModalOpen(true);
  };

  const handleSaveNoteModal = async () => {
    if (!selectedStudentForNote) return;
    const studentId = selectedStudentForNote?.studentId || selectedStudentForNote?._id;
    try {
      setSavingNote(true);
      const res = await updateEligibleNote({ studentId, note: noteInputText });
      if (res?.status) {
        ShowToast("Admin note updated successfully", Severty.SUCCESS);
        setEligibles((prev) =>
          prev.map((item) =>
            (item.studentId === studentId || item._id === studentId)
              ? { ...item, adminNotes: noteInputText }
              : item
          )
        );
        setNoteModalOpen(false);
      }
    } catch (err) {
      ShowToast(err?.message || "Failed to update note", Severty.ERROR);
    } finally {
      setSavingNote(false);
    }
  };

  const eligiblesColumn = [
    { title: "Student", dataIndex: "name", key: "student" },
    {
      title: "Current Belt",
      dataIndex: "belt",
      key: "belt",
      render: (belt, record) => {
        const beltName =
          record?.beltName ||
          record?.belt?.name ||
          record?.beltId?.name ||
          (typeof belt === "object" ? belt?.name : belt);
        return beltName || "-";
      },
    },
    {
      title: "Attendance",
      dataIndex: "attendance",
      key: "attendance",
      render: (val, record) => record?.attendance ?? record?.totalAttendance ?? 0,
    },
    {
      title: "Exam Date",
      dataIndex: "examDate",
      key: "examDate",
      render: (date, record) => {
        if (record?.examDateTime) {
          const m = moment(record.examDateTime);
          if (m.isValid()) return m.format("DD-MM-YYYY");
        }
        if (date && record?.examTime) {
          const utcM = moment.utc(`${date} ${record.examTime}`, [
            "DD-MM-YYYY hh:mm A",
            "DD/MM/YYYY hh:mm A",
            "DD-MM-YYYY HH:mm",
            "DD/MM/YYYY HH:mm",
          ]);
          if (utcM.isValid()) return utcM.local().format("DD-MM-YYYY");
        }
        return date || "-";
      },
    },
    {
      title: "Exam Time",
      dataIndex: "examTime",
      key: "examTime",
      render: (time, record) => {
        if (record?.examDateTime) {
          const m = moment(record.examDateTime);
          if (m.isValid()) return m.format("hh:mm A");
        }
        if (time && record?.examDate) {
          const utcM = moment.utc(`${record.examDate} ${time}`, [
            "DD-MM-YYYY hh:mm A",
            "DD/MM/YYYY hh:mm A",
            "DD-MM-YYYY HH:mm",
            "DD/MM/YYYY HH:mm",
          ]);
          if (utcM.isValid()) return utcM.local().format("hh:mm A");
        }
        return time || "-";
      },
    },
    {
      title: "Admin Notes",
      dataIndex: "adminNotes",
      key: "adminNotes",
      render: (text, record) => {
        return (
          <div
            style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}
            onClick={() => openNoteModal(record)}
          >
            {record?.adminNotes ? (
              <>
                <Tooltip title={record.adminNotes}>
                  <span
                    style={{
                      maxWidth: 160,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      color: "#1890ff",
                      textDecoration: "underline",
                      fontWeight: 500,
                    }}
                  >
                    {record.adminNotes}
                  </span>
                </Tooltip>
                <Button
                  type="text"
                  size="small"
                  icon={<i className="fas fa-edit" style={{ color: "#1890ff", fontSize: 13 }} />}
                />
              </>
            ) : (
              <Button
                size="small"
                type="dashed"
                icon={<i className="fas fa-plus" style={{ marginRight: 4 }} />}
              >
                Add Note
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  const [leaderboardData, setLeaderboardData] = useState({});
  const [filters, setFilters] = useFilter({
    sortField: "createdOn", sort: "desc", name: "",
  });

  const count = [
    {
      today: "Total Users",
      title: `${dashboard && dashboard.user ? dashboard.user : 0}`,
      persent: "10%",
      icon: <i class="fas fa-user-friends"></i>,
      bnb: "bnb2",
      url: "/students",
      key: "totalCustomer",
    },
    {
      today: "Active Users",
      title: `${dashboard && dashboard.activeUser ? dashboard.activeUser : 0}`,
      icon: <i class="fas fa-copyright"></i>,
      bnb: "bnb2",
      url: "/students",
      key: "staff",
    },
    {
      today: "Total Employees",
      title: `${dashboard && dashboard.employee ? dashboard.employee : 0}`,
      icon: <i class="fas fa-copyright"></i>,
      bnb: "bnb2",
      url: "/employees",
      key: "staff",
    },
    {
      today: " Upcoming Events",
      title: `${dashboard && dashboard.events ? dashboard.events : 0}`,
      icon: <i class="fas fa-copyright"></i>,
      bnb: "bnb2",
      url: "/event",
      key: "staff",
    },
    {
      today: "Total branches",
      title: `${dashboard && dashboard.totalBranches ? dashboard.totalBranches : 0
        }`,
      icon: <i class="fas fa-copyright"></i>,
      bnb: "bnb2",
      url: "/branches",
      key: "staff",
    },
    {
      today: "Total Subscription",
      title: `${dashboard && dashboard.totalSubscriptions ? dashboard.totalSubscriptions : 0
        }`,
      icon: <i class="fas fa-copyright"></i>,
      bnb: "bnb2",
      url: "/subscriptions",
      key: "staff",
    },
    {
      today: "Total Transaction",
      title: `${dashboard && dashboard.totalTransactions ? dashboard.totalTransactions : 0
        }`,
      icon: <i class="fas fa-copyright"></i>,
      bnb: "bnb2",
      url: "/transactions",
      key: "staff",
    },
    {
      today: "Total Amount (CAD)",
      title: `${dashboard && dashboard.totalAmount ? dashboard.totalAmount : 0
        }`,
      icon: <i class="fas fa-copyright"></i>,
      bnb: "bnb2",
      url: "/transactions",
      key: "staff",
    },
  ];

  useApi({
    callback: getDashboardCount,
    payload: { ...filters },
    dependencies: [filters],
    shouldCallApi: true,
    onSuccess: (data) => {
      setDashboard(data);
      setLoading(false);
    },
    onError: () => {
      setLoading(false);
    },
  });

  useApi({
    callback: getLeaderboardData,
    payload: { ...filters },
    dependencies: [filters],
    shouldCallApi: true,
    onSuccess: (data) => {
      setLeaderboardData(data);
      setLoading(false);
    },
    onError: () => {
      setLoading(false);
    },
  });

  useApi({
    callback: ListBooking,
    payload: { status: "accepted" },
    dependencies: [],
    shouldCallApi: true,
    onSuccess: (data) => {
      setBookings(data?.docs || []);
    },
    onError: (err) => {
      ShowToast(err, Severty.ERROR);
    },
  });

  const { loading: isEligiblesLoading } = useApi({
    callback: allEligibles,
    payload: eligiblesFilters,
    dependencies: [eligiblesFilters],
    shouldCallApi: true,
    onSuccess: (res) => {
      const payload = res?.data || res;
      if (payload?.eligibles) {
        setEligibles(payload.eligibles || []);
        setTotalEligibles(payload.totalDocs ?? payload.overview?.totalEligibleStudents ?? payload.eligibles.length);
        if (payload.overview) {
          setExamOverview(payload.overview);
        }
      } else if (Array.isArray(payload)) {
        setEligibles(payload);
        setTotalEligibles(payload.length);
      }
    },
    onError: (err) => {
      ShowToast(err, Severty.ERROR);
    },
  });

  const handleDateRangeChange = (dates, dateStrings) => {
    const [startDate, endDate] = dateStrings;
    setFilters((prev) => ({
      ...prev,
      startDate,
      endDate,
    }));
  };

  return (
    <>
      {userProfile &&
        !userProfile?.permission?.includes("dashboard-management") &&
        userProfile.type === "SubAdmin" ? (
        <Row>
          <Col xs={24} sm={24} md={24}>
            <Descriptions
              column={{ xs: 16, sm: 16, md: 24 }}
              title="Sub Admin Info"
            >
              <Descriptions.Item label="Name">
                {userProfile.name}
              </Descriptions.Item>
              <Descriptions.Item label="Role title">
                {userProfile.title}
              </Descriptions.Item>
              <Descriptions.Item label="Mobile Number">
                {formatPhone(
                  userProfile.country_code,
                  userProfile.mobile_number
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Cities">
                {userProfile.city_ids.map((item) => item.name).join(", ")}
              </Descriptions.Item>
            </Descriptions>
          </Col>
        </Row>
      ) : (
        <div className="page-top-space home-card layout-content">
          <div className="mb-24 mt-25">
            <div className="sub_title">
              <div className="bussiness_year">
                <RangePicker
                  style={{ width: 200 }}
                  format="DD/MM/YYYY"
                  onChange={handleDateRangeChange}
                />
              </div>
            </div>
            <Row gutter={[24, 0]}>
              <Col xs={24} sm={24} md={24}>
                <Row
                  className="rowgap-vbox"
                  gutter={[24, 16]}
                >
                  {count.map((c, index) => (
                    <Col
                      key={index}
                      xs={24}
                      sm={24}
                      md={12}
                      lg={12}
                      xl={6}
                      xxl={6}
                      className="mb24"
                    >
                      <CountCard c={c} key={index} loading={loading} />
                    </Col>
                  ))}
                </Row>
              </Col>
            </Row>

            <div>
              <UserAndBokkingChart>  </UserAndBokkingChart>
            </div>

            <Row gutter={[24, 0]} className="mt-3">
              <Col xs={24}>
                <CustomTable
                  columns={columns}
                  fields={false}
                  data={bookings}
                  loading={loading}
                  heading={
                    <Title level={3} className="p-2">
                      Upcoming Class
                    </Title>
                  }
                  pagination={false}
                />
              </Col>
            </Row>
            <Row gutter={[24, 0]} className="mt-3">
              <Col xs={24}>
                <Title level={4} className="p-2">
                  Eligible Students for Graduation
                </Title>
                <Row gutter={[16, 16]} className="mb-3 mt-2">
                  <Col xs={24} sm={12} md={6}>
                    <Card bordered={false} className="criclebox" style={{ height: "100%" }}>
                      <div className="number">
                        <Row align="middle" justify="space-between">
                          <Col xs={18}>
                            <span>Total Upcoming Exam Capacity</span>
                            <Title level={3}>{examOverview?.totalUpcomingExamCapacity ?? 0}</Title>
                          </Col>
                          <Col xs={6} style={{ textAlign: "right", fontSize: "24px", color: "#1890ff" }}>
                            <i className="fas fa-users"></i>
                          </Col>
                        </Row>
                      </div>
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Card bordered={false} className="criclebox" style={{ height: "100%" }}>
                      <div className="number">
                        <Row align="middle" justify="space-between">
                          <Col xs={18}>
                            <span>Total Reserved Exam Spots</span>
                            <Title level={3}>{examOverview?.totalReservedExamSpots ?? 0}</Title>
                          </Col>
                          <Col xs={6} style={{ textAlign: "right", fontSize: "24px", color: "#52c41a" }}>
                            <i className="fas fa-calendar-check"></i>
                          </Col>
                        </Row>
                      </div>
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Card bordered={false} className="criclebox" style={{ height: "100%" }}>
                      <div className="number">
                        <Row align="middle" justify="space-between">
                          <Col xs={18}>
                            <span>Total Eligible Students</span>
                            <Title level={3}>{examOverview?.totalEligibleStudents ?? 0}</Title>
                          </Col>
                          <Col xs={6} style={{ textAlign: "right", fontSize: "24px", color: "#722ed1" }}>
                            <i className="fas fa-user-graduate"></i>
                          </Col>
                        </Row>
                      </div>
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Card bordered={false} className="criclebox" style={{ height: "100%" }}>
                      <div className="number">
                        <Row align="middle" justify="space-between">
                          <Col xs={18}>
                            <span>Eligible Students Without a Reservation</span>
                            <Title level={3}>{examOverview?.eligibleStudentsWithoutReservation ?? 0}</Title>
                          </Col>
                          <Col xs={6} style={{ textAlign: "right", fontSize: "24px", color: "#faad14" }}>
                            <i className="fas fa-user-clock"></i>
                          </Col>
                        </Row>
                      </div>
                    </Card>
                  </Col>
                </Row>
                <CustomTable
                  columns={eligiblesColumn}
                  data={eligibles}
                  fields={false}
                  filters={eligiblesFilters}
                  setFilters={setEligiblesFilters}
                  totalDocs={totalEligibles}
                  rowKey={(record) => record._id || record.studentId || record.id}
                  loading={isEligiblesLoading}
                  pagination={true}
                />
              </Col>
            </Row>

            <Row gutter={[24, 0]} className="mt-3">
              <Col xs={24}>
                <CustomTable
                  columns={leaderboardColumns}
                  data={leaderboardData?.topPerformer || []}
                  fields={false}
                  heading={
                    <Title level={4} className="p-2">
                      Top Performer Students
                    </Title>
                  }
                  pagination={false}
                />
              </Col>
            </Row>

            <Row gutter={[24, 0]} className="mt-3">
              <Col xs={24}>
                <CustomTable
                  fields={false}
                  columns={leaderboardColumns}
                  data={leaderboardData?.studentsRequireAttention || []}
                  heading={
                    <Title level={4} className="p-2">
                      Students Requiring Attention
                    </Title>
                  }
                  pagination={false}
                />
              </Col>
            </Row>
          </div>
        </div>
      )}

      <Modal
        title={
          <span>
            <i className="fas fa-sticky-note" style={{ marginRight: 8, color: "#faad14" }} />
            Admin Note - {selectedStudentForNote?.name || "Student"}
          </span>
        }
        open={noteModalOpen}
        onCancel={() => setNoteModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setNoteModalOpen(false)}>
            Cancel
          </Button>,
          <Button key="save" type="primary" loading={savingNote} onClick={handleSaveNoteModal}>
            Save
          </Button>,
        ]}
      >
        <div style={{ marginBottom: 12 }}>
          <Typography.Text type="secondary">
            Enter administrative note for this student's current graduation eligibility cycle:
          </Typography.Text>
        </div>
        <Input.TextArea
          rows={4}
          placeholder="Enter admin note..."
          value={noteInputText}
          onChange={(e) => setNoteInputText(e.target.value)}
          maxLength={500}
          showCount
        />
      </Modal>
    </>
  );
}

const CountCard = ({ c, loading }) => {
  const { userProfile } = useAuthContext();
  const navigate = useNavigate();

  return (
    <Card
      hoverable
      bordered={false}
      className="criclebox"
      style={{ height: "100%" }}
      loading={loading}
      onClick={() => {
        userProfile?.type !== "SubAdmin" && c.url && navigate(c.url);
      }}
    >
      <div className="number">
        <Row align="middle" gutter={[24, 0]}>
          <Col xs={18}>
            <span>{lang(c?.today)}</span>
            <Title level={3}>{lang(c?.title)}</Title>
          </Col>
        </Row>
      </div>
    </Card>
  );
};

export default Home;
